import React, { useRef, useState } from 'react';

import styled, { useTheme } from 'styled-components';

import { CircleDelete } from '../Icons';

type stateType = 'default' | 'typing' | 'completed' | 'error';

type Props = {
  state?: stateType;
  onChange?: () => void;
  label?: string;
  textGuide?: string;
  defaultValue?: string;
  placeholder?: string;
  isFocus?: boolean;
  width?: number;
};

const TextField = ({
  onChange,
  label,
  state = 'default',
  textGuide,
  defaultValue,
  placeholder,
  isFocus,
  width,
}: Props) => {
  const [_isFocus, setFocus] = useState(isFocus);
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setValue(value);
    onChange?.();
  };

  const handleClickDeleteButton = () => {
    inputRef.current.focus();
    inputRef.current.value = '';
    setValue('');
  };

  return (
    <SWrapper $state={state} $isFocus={_isFocus} style={{ width }}>
      <label>{label}</label>
      <div className="input-box">
        <input
          autoFocus
          ref={inputRef}
          type="text"
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          placeholder={placeholder || ''}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
        />
        {value?.length > 0 && _isFocus && (
          <button className="delete-btn" onClick={handleClickDeleteButton}>
            <CircleDelete size={20} color={theme.color.icon.icon_3} />
          </button>
        )}
      </div>
      {textGuide && <p className="guide-text">{textGuide}</p>}
    </SWrapper>
  );
};
export default TextField;

const SWrapper = styled.div<{ $state: stateType; $isFocus: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  label {
    color: ${({ theme }) => theme.color.text.TXT_3};
    ${({ theme }) => theme.font.body2_sb};
  }

  .input-box {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 13px 12px;
    height: 48px;
    border-radius: 8px;
    background: ${({ theme }) => theme.color.field.input};
    box-sizing: border-box;

    ${({ $state, theme, $isFocus }) => {
      if ($state === 'error') {
        return `border: 1px solid ${theme.palette.pink[50]};`;
      } else if ($isFocus || $state === 'typing') {
        return `border: 1px solid ${theme.color.static.primary};`;
      }
    }}

    > input {
      width: 100%;
      color: ${({ theme }) => theme.color.text.TXT_4};
      ${({ theme }) => theme.font.body2_m};
      outline: none;
      caret-color: ${({ theme }) => theme.color.static.primary};

      ::placeholder {
        color: ${({ theme }) => theme.color.text.default};
        ${({ theme }) => theme.font.body2_r};
      }
    }
  }

  .guide-text {
    padding: 0px 12px;
    color: ${({ theme }) => theme.color.text.TXT_1};
    ${({ theme }) => theme.font.caption1_m};

    ${({ $state, theme }) => {
      if ($state === 'error') {
        return `color: ${theme.palette.pink[50]};`;
      }
    }}
  }

  .delete-btn {
    width: 20px;
    height: 20px;
  }
`;
